import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import { useProjects } from "../../context/ProjectContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { MagnifyingGlass, ArrowRight, Command as CommandIcon, X } from "@phosphor-icons/react";

function highlight(text, q) {
  if (!text || !q) return text || "";
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 text-neutral-900 rounded-sm px-0.5">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

const STATUS_COLORS = {
  "Completed": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "In Progress": "bg-blue-100 text-blue-800 border-blue-200",
  "Not Started": "bg-neutral-100 text-neutral-700 border-neutral-200",
  "On Hold": "bg-amber-100 text-amber-800 border-amber-200",
  "Blocked": "bg-red-100 text-red-800 border-red-200",
};

export default function GlobalSearch({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const reqSeq = useRef(0);
  const navigate = useNavigate();
  const { setProjectId } = useProjects();

  // Focus input when opened; reset when closed
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 10);
    } else {
      setQ("");
      setResults([]);
      setTotal(0);
      setActiveIdx(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    const mySeq = ++reqSeq.current;
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/tasks/search/global", { params: { q: term, limit: 15 } });
        if (mySeq !== reqSeq.current) return; // stale
        setResults(data.results || []);
        setTotal(data.total || 0);
        setActiveIdx(0);
      } catch {
        if (mySeq !== reqSeq.current) return;
        setResults([]);
        setTotal(0);
      } finally {
        if (mySeq === reqSeq.current) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [q]);

  const handleSelect = useCallback((r) => {
    if (!r || !r.project) return;
    setProjectId(r.project.id);
    onOpenChange(false);
    // Deep link into the dashboard with task focus and search filter set to object_id
    const objId = r.task.object_id || "";
    const params = new URLSearchParams();
    params.set("project_id", r.project.id);
    if (objId) params.set("focus", r.task.id);
    if (objId) params.set("search", objId);
    navigate(`/?${params.toString()}`);
  }, [navigate, onOpenChange, setProjectId]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = results[activeIdx];
      if (r) handleSelect(r);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 max-w-2xl overflow-hidden"
        data-testid="global-search-dialog"
      >
        <DialogTitle className="sr-only">Global task search</DialogTitle>
        <DialogDescription className="sr-only">
          Search tasks by Object ID, title, or module across all projects you have access to.
        </DialogDescription>
        <div className="flex items-center gap-3 px-4 h-14 border-b border-neutral-200">
          <MagnifyingGlass size={18} weight="bold" className="text-neutral-500 shrink-0" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search tasks by Object ID, title, or module across all projects…"
            className="flex-1 h-10 bg-transparent outline-none text-sm placeholder:text-neutral-400"
          />
          {loading ? (
            <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-mono">searching…</span>
          ) : null}
          <button
            onClick={() => onOpenChange(false)}
            className="text-neutral-400 hover:text-neutral-700 p-1"
            data-testid="global-search-close-btn"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="max-h-[420px] overflow-y-auto" data-testid="global-search-results">
          {!q.trim() ? (
            <div className="px-6 py-10 text-center text-sm text-neutral-500">
              <MagnifyingGlass size={28} weight="thin" className="text-neutral-300 mx-auto mb-2" />
              Start typing an Object ID, title, or module to find any task.
              <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-neutral-400 font-mono uppercase tracking-widest">
                <kbd className="px-1.5 py-0.5 rounded border border-neutral-200 bg-neutral-50">↑</kbd>
                <kbd className="px-1.5 py-0.5 rounded border border-neutral-200 bg-neutral-50">↓</kbd>
                <span>navigate</span>
                <kbd className="ml-2 px-1.5 py-0.5 rounded border border-neutral-200 bg-neutral-50">↵</kbd>
                <span>open</span>
                <kbd className="ml-2 px-1.5 py-0.5 rounded border border-neutral-200 bg-neutral-50">Esc</kbd>
                <span>close</span>
              </div>
            </div>
          ) : results.length === 0 && !loading ? (
            <div
              className="px-6 py-10 text-center text-sm text-neutral-500"
              data-testid="global-search-empty"
            >
              No tasks match <span className="font-semibold text-neutral-800">&quot;{q}&quot;</span> in any of your projects.
            </div>
          ) : (
            <ul className="py-2">
              {results.map((r, idx) => {
                const t = r.task;
                const isActive = idx === activeIdx;
                const archived = r.project?.status === "Inactive";
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => handleSelect(r)}
                      data-testid={`global-search-result-${idx}`}
                      className={`w-full text-left px-4 py-3 flex items-start gap-3 border-l-2 transition-colors ${
                        isActive
                          ? "bg-neutral-50 border-neutral-900"
                          : "border-transparent hover:bg-neutral-50"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-neutral-900 text-white">
                            {highlight(t.object_id || "—", q)}
                          </span>
                          {t.module ? (
                            <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 border border-neutral-200 rounded px-1.5 py-0.5">
                              {highlight(t.module, q)}
                            </span>
                          ) : null}
                          {t.overall_status ? (
                            <span
                              className={`text-[10px] font-medium uppercase tracking-wider border rounded px-1.5 py-0.5 ${
                                STATUS_COLORS[t.overall_status] || "bg-neutral-100 text-neutral-700 border-neutral-200"
                              }`}
                            >
                              {t.overall_status}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 text-sm font-medium text-neutral-900 truncate">
                          {highlight(t.title || "(untitled)", q)}
                        </div>
                        <div className="mt-1 text-xs text-neutral-500 truncate">
                          {r.customer?.name || "—"}
                          <span className="mx-1.5 text-neutral-300">/</span>
                          <span className="font-medium text-neutral-700">
                            {r.project?.name || "—"}
                          </span>
                          {r.project?.code ? (
                            <span className="ml-1.5 text-[10px] font-mono text-neutral-400 uppercase">
                              {r.project.code}
                            </span>
                          ) : null}
                          {archived ? (
                            <span className="ml-2 text-[9px] font-bold uppercase tracking-wider text-amber-700 border border-amber-200 bg-amber-50 rounded px-1.5 py-0.5">
                              Archived
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <ArrowRight
                        size={14}
                        weight="bold"
                        className={`shrink-0 mt-1 transition-opacity ${isActive ? "opacity-100" : "opacity-30"}`}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {q.trim() && results.length > 0 ? (
          <div className="flex items-center justify-between px-4 py-2 border-t border-neutral-200 bg-neutral-50 text-[11px] font-mono uppercase tracking-widest text-neutral-500">
            <span data-testid="global-search-count">
              {results.length} of {total} match{total === 1 ? "" : "es"}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded border border-neutral-200 bg-white">↵</kbd>
              open task
            </span>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** Header trigger button paired with GlobalSearch dialog. Handles ⌘K / Ctrl+K. */
export function GlobalSearchTrigger() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="global-search-btn"
        className="h-9 gap-2 text-neutral-500 hover:text-neutral-900 min-w-[220px] justify-start font-normal"
      >
        <MagnifyingGlass size={14} weight="bold" />
        <span className="text-xs">Search all tasks…</span>
        <span className="ml-auto flex items-center gap-0.5 text-[10px] text-neutral-400 font-mono">
          <CommandIcon size={10} weight="bold" />K
        </span>
      </Button>
      <GlobalSearch open={open} onOpenChange={setOpen} />
    </>
  );
}
